// SPA 入口：装载样式 + QueryClient + Router。
// routeTree.gen.ts 由 @tanstack/router-plugin 在 vite 启动/构建时生成（见 .gitignore）。

import "./styles/theme.css";
import "./styles/status.css";
import "./styles/base.css";

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { Toaster } from "sonner";

import { routeTree } from "./routeTree.gen";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
});

const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  parseSearch: parsePlainSearch,
  stringifySearch: stringifyPlainSearch,
  context: { queryClient },
});

function parsePlainSearch(searchStr: string): Record<string, string | string[]> {
  const raw = searchStr.startsWith("?") ? searchStr.slice(1) : searchStr;
  const params = new URLSearchParams(raw);
  const out: Record<string, string | string[]> = {};
  for (const [key, value] of params) {
    const current = out[key];
    if (current === undefined) {
      out[key] = value;
    } else if (Array.isArray(current)) {
      current.push(value);
    } else {
      out[key] = [current, value];
    }
  }
  return out;
}

function stringifyPlainSearch(search: Record<string, unknown>): string {
  const params = new URLSearchParams();
  Object.entries(search).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item !== undefined && item !== null && item !== "") params.append(key, String(item));
      });
      return;
    }
    params.set(key, typeof value === "object" ? JSON.stringify(value) : String(value));
  });
  const text = params.toString();
  return text ? `?${text}` : "";
}

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("#root 元素未找到");

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      <Toaster richColors position="top-right" />
    </QueryClientProvider>
  </StrictMode>,
);
